/**
 * Mansion maps — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mansionMapsPack: ToilePack = {
    slug: 'mansion-maps',
    category: 'tabletop',
    name: 'Mansion maps',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'ground', label: 'Ground floor', subject: "a top-down floor plan of a manor's ground floor on a square grid, an entrance hall with a grand staircase, a library, a dining room and a conservatory"},
        {key: 'upper', label: 'Upper floor', subject: "a top-down floor plan of a manor's upper floor on a square grid, a long corridor of bedrooms, a study and a narrow servants' stair"},
        {key: 'grounds', label: 'Grounds', subject: "a top-down map of a manor's grounds, the house outline in the middle, a hedge maze, a family crypt and a carriage house"},
    ],
    styles: [
        {key: 'grid', label: 'Clean grid', style: 'crisp uniform black ink line art, thin square grid, closed wall outlines, no shading', palette: 'black ink only', framing: 'on a pure white background, the full page used with a white margin'},
        {key: 'inked', label: 'Hand-inked', style: 'old-school hand-inked cartographer drawing, thick black walls, crosshatched solid rock, stippled floors, faint grid', palette: 'black ink only', framing: 'on plain white paper, the full page used with a white margin'},
        {key: 'inverse', label: 'White on black', style: 'blueprint-style white line art with thin uniform strokes and a faint square grid, closed wall outlines, no fills', palette: 'white lines only', framing: 'on a solid black background, the full page used with a black margin'},
    ],
    directions: ['basement', 'victorian', 'with ghosts'],
    directionHint: 'try: basement',
    seo: {
        title: 'Mansion maps: ground floor, upper floor, grounds',
        metaDescription: "Printable mansion maps in three styles: a manor's ground floor, its upper floor of bedrooms and the grounds with a garden.",
        h1: 'Mansion maps: manor ground floor, upper floor, grounds',
        tagline: 'Nine maps: the ground floor, the upper floor and the grounds of a manor, in three styles.',
        intro: "A mansion map is for a haunted house, a heist or a murder mystery, where every room must be laid out. This pack shows a manor's ground floor with a hall and library, the upper floor of bedrooms, and the grounds with a hedge maze and a crypt. Each comes as a clean grid, a hand-inked plan or white lines on black. Type a house, such as a basement or a Victorian house, and the plan keeps its grid.",
        useCases: [
            'a haunted house for a party game',
            'a heist plan through the ground floor',
            'a bedroom corridor for a murder mystery',
            'the grounds for a chase outside the house',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: "Three readings in three styles: a manor's ground floor, its upper floor of bedrooms, and the grounds with a hedge maze and a crypt."},
            {q: 'Does it cover the outdoors?', a: 'Yes. The grounds picture shows the house outline in the middle, with a hedge maze, a family crypt and a carriage house around it.'},
            {q: 'Which styles print in black?', a: 'Clean grid and hand-inked use black ink on white paper. White on black is blueprint-style white lines on a solid black background.'},
        ],
    },
}
